import { difference, flatten, pick, pull, union, uniq, unset, xor } from 'lodash'
import moment from 'moment'

import { dateToString, stringToDate } from 'modules/core/lib/dates'

import type { Leave, RestDay, Shift, ShiftsRow, View } from 'modules/shift_management/lib/types'
import PlanningVersions from 'modules/shift_management/store/internal/collections/PlanningVersions'
import { GridSelection } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

export type DaysByRow = { [key in string]: string[] }
type ColumnDaysSelected = string[]
type RowsSelected = string[]

type Shifts = Record<string, Shift>
type RestDays = Record<string, RestDay>

export interface SelectionState {
  shifts: Shifts
  restDays: RestDays
  leave: Leave | null
  daysByRow: DaysByRow
  isEnabled: boolean
  columnDaysSelected: ColumnDaysSelected
  rowsSelected: RowsSelected
  allTableSelected: boolean
  planningVersions: PlanningVersions
  showEmptyShiftCell: boolean
  isResolvingGridSelection: boolean
  kind?: GridSelection['kind']
}

export type SelectionDispatch = (arg0: SelectionAction) => void

const resetGridSelectionMeta = (state: SelectionState): SelectionState => ({
  ...state,
  kind: undefined,
  isResolvingGridSelection: false,
})

export type SelectionAction =
  | {
      type: 'CLEAR_SELECTION'
    }
  | {
      type: 'SHOW_EMPTY_SHIFT_CELL'
    }
  | {
      type: 'HIDE_EMPTY_SHIFT_CELL'
    }
  | {
      type: 'TOGGLE_SHIFT'
      shift: Shift
      selectedDay: moment.Moment
      view: View
    }
  | {
      type: 'TOGGLE_REST_DAY'
      restDay: RestDay
      view: View
    }
  | {
      type: 'TOGGLE_DAY'
      day: moment.Moment
      shiftsRow: ShiftsRow
      view: View
      forcedValue?: boolean
    }
  | {
      type: 'TOGGLE_DAY_COLUMN'
      day: moment.Moment
      shiftsRows: ShiftsRow[]
      view: View
    }
  | {
      type: 'TOGGLE_SHIFTS_ROW'
      shiftsRow: ShiftsRow
      view: View
    }
  | {
      type: 'TOGGLE_ALL_SHIFTS_ROWS'
      shiftsRows: ShiftsRow[]
      view: View
    }
  | {
      type: 'SELECT_RANGE'
      startOn: moment.Moment
      endOn: moment.Moment
      shiftsRow: ShiftsRow
      view: View
    }
  | {
      type: 'TOGGLE_LEAVE'
      leave: Leave
    }
  | {
      type: 'SET_FROM_GRID'
      daysByRow: DaysByRow
      shifts?: Shifts
      restDays?: RestDays
      kind: GridSelection['kind']
    }
  | {
    type: 'RESOLVE_GRID_SELECTION',
    isResolvingGridSelection: boolean
  }

function toggleDay(
  daysByRow: DaysByRow,
  shiftRowKey: string,
  dayId: string,
  forcedValue?: boolean
): DaysByRow {
  const prevValue = daysByRow[shiftRowKey] || []
  const newState: DaysByRow = {
    ...daysByRow,
    [shiftRowKey]: forcedValue ? union(prevValue, [dayId]) : xor(prevValue, [dayId]),
  }
  const newStateValue = newState[shiftRowKey] || []

  // If the new row has no shifts, remove it from the selection
  if (newStateValue.length === 0) {
    const { [shiftRowKey]: discarded, ...rest } = newState

    return rest
  }

  return newState
}

function toggleColumnDayByRow(
  columnDaysSelected: ColumnDaysSelected,
  daysByRow: DaysByRow,
  shiftsRowsWithoutLeave: ShiftsRow[],
  shiftsRows: ShiftsRow[],
  dayId: string,
  view: View
): DaysByRow {
  const day = stringToDate(dayId, view.dateFormat)
  const newState: DaysByRow = { ...daysByRow }

  if (columnDaysSelected.includes(dayId)) {
    // remove dayId from daysByRow
    for (const rowKey in newState) {
      newState[rowKey] = pull(newState[rowKey] || [], dayId)
      const newStateValue = newState[rowKey]

      if (newStateValue?.length === 0) {
        unset(newState, rowKey)
      }
    }
  } else {
    // TODO: refactor this logic to avoid the extra conditional,
    // at the moment too coupled with employees

    // add dayId to employeeIds based on planning version
    if (view.isByEmployees) {
      shiftsRowsWithoutLeave.forEach((shiftRow: ShiftsRow) => {
        const planningVersion = shiftRow.planningVersions.planningVersionWithinDate(
          shiftRow.id,
          day
        )
        if (planningVersion?.isShiftManagementTool) {
          newState[shiftRow.key] = union(newState[shiftRow.key] || [], [dayId])
        }
      })
    } else {
      // add dayId to workAreaKeys only if that work area can be managed/not archived
      shiftsRows.forEach((shiftRow: ShiftsRow) => {
        if (shiftRow.canManage) {
          newState[shiftRow.key] = union(newState[shiftRow.key] || [], [dayId])
        }
      })
    }
  }

  return newState
}

function toggleColumnShifts(
  columnDaysSelected: ColumnDaysSelected,
  shiftsRows: ShiftsRow[],
  shifts: Shifts,
  day: moment.Moment,
  firstDay: moment.Moment,
  lastDay: moment.Moment,
  view: View
) {
  const dayShifts = shiftsRows.flatMap((shiftRow: ShiftsRow) => {
    if (shiftRow.canManageOn(day)) {
      return shiftRow.shifts.filterByDate(day, view)
    } else {
      return []
    }
  })

  const newState = { ...shifts }
  const dayId = dateToString(day, view.dateFormat)
  const startAt = firstDay.clone().startOf('day')
  const endAt = lastDay.clone().endOf('day')

  const dayShiftsByRange = dayShifts.filter((shift) => shift.inRange(startAt, endAt))

  if (columnDaysSelected.includes(dayId)) {
    // toggle off column shifts
    const remainingShiftIds = difference(
      Object.keys(newState),
      dayShiftsByRange.map((shift: Shift) => shift.referenceKey(day))
    )

    return pick(newState, remainingShiftIds)
  } else {
    // add column shifts to selection
    dayShiftsByRange.forEach((shift: Shift) => {
      newState[shift.referenceKey(day)] = shift
    })
  }

  return newState
}

function toggleColumnRestDays(
  columnDaysSelected: ColumnDaysSelected,
  shiftsRows: ShiftsRow[],
  restDays: RestDays,
  day: moment.Moment,
  view: View
) {
  const dayRestDays = shiftsRows.flatMap((shiftRow: ShiftsRow) => {
    if (shiftRow.canManageOn(day)) {
      return shiftRow.restDays.filterByDate(day)
    } else {
      return []
    }
  })
  const newState = { ...restDays }
  const dayId = dateToString(day, view.dateFormat)

  if (columnDaysSelected.includes(dayId)) {
    // toggle off column rest days
    const remainingRestDayIds = difference(
      Object.keys(newState),
      dayRestDays.map((restDay: RestDay) => String(restDay.id))
    )

    return pick(newState, remainingRestDayIds)
  } else {
    // add column rest days to selection
    dayRestDays.forEach((restDay: RestDay) => {
      newState[String(restDay.id)] = restDay
    })
  }

  return newState
}

function selectRow(
  rowsSelected: RowsSelected,
  daysByRow: DaysByRow,
  days: string[],
  rowKey: string
) {
  const newState = { ...daysByRow }

  if (rowsSelected.includes(rowKey)) {
    // toggle off row
    unset(newState, rowKey)
  } else {
    // toggle on row
    newState[rowKey] = days
  }

  return newState
}

function toggleRowShifts(
  rowsSelected: RowsSelected,
  shifts: Shifts,
  shiftsRow: ShiftsRow,
  firstDay: moment.Moment,
  lastDay: moment.Moment
) {
  const newState = shifts
  const startAt = firstDay.clone().startOf('day')
  const endAt = lastDay.clone().endOf('day')
  const shiftsByRange = shiftsRow.shifts.filterByRange(startAt, endAt)

  if (rowsSelected.includes(shiftsRow.key)) {
    const shiftIds = shiftsByRange.shifts.flatMap((shift: Shift) => {
      if (shift.isNightShift) {
        const shifts = [shift.beginningReference]

        if (shift.isOvernightByCalendarDay) {
          shifts.push(shift.endingReference)
        }

        return shifts
      } else {
        return String(shift.id)
      }
    })

    // toggle off shifts
    const remainingShiftIds = difference(Object.keys(newState), shiftIds)

    return pick(newState, remainingShiftIds)
  } else {
    // add shifts to selection
    shiftsByRange.shifts.forEach((shift: Shift) => {
      shifts = addShifts(shiftsRow, newState, shift)
    })
  }

  return newState
}

function toggleRowRestDays(
  rowsSelected: RowsSelected,
  restDays: RestDays,
  shiftsRow: ShiftsRow,
  firstDay: moment.Moment,
  lastDay: moment.Moment
) {
  const newState = restDays
  const startAt = firstDay.clone().startOf('day')
  const endAt = lastDay.clone().endOf('day')
  const restDaysByRange = shiftsRow.restDays.filterByRange(startAt, endAt)

  if (rowsSelected.includes(shiftsRow.key)) {
    const restDayIds = restDaysByRange.restDays.flatMap((restDay: RestDay) => {
      return String(restDay.id)
    })

    // toggle off rest days
    const remainingRestDayIds = difference(Object.keys(newState), restDayIds)

    return pick(newState, remainingRestDayIds)
  } else {
    // add rest days to selection
    restDaysByRange.restDays.forEach((restDay: RestDay) => {
      restDays = addRestDays(shiftsRow, newState, restDay)
    })
  }

  return newState
}
const toggleDayColumn = (
  state: SelectionState,
  shiftsRows: ShiftsRow[],
  day: moment.Moment,
  view: View
) => {
  const dates = view.dates
  const dayId = dateToString(day, view.dateFormat)
  const firstDay = dates[0] ?? moment()
  const lastDay = dates[dates.length - 1] ?? moment()

  const shiftsRowsWithoutLeave = shiftsRows.filter((shiftRow: ShiftsRow) => {
    if (!shiftRow.canManage) return false

    const dayLeaves = shiftRow.leaves.filterByDate(day)
    const approvedNonWorkableFullDays = dayLeaves.filter(
      (leave: Leave) => !leave.workable && !leave.hourly && !leave.halfDay && leave.approved
    )

    return approvedNonWorkableFullDays.length === 0
  })

  const toggledColumnDayByEmployee: DaysByRow = toggleColumnDayByRow(
    state.columnDaysSelected,
    state.daysByRow,
    shiftsRowsWithoutLeave,
    shiftsRows,
    dayId,
    view
  )

  const toggledColumnShifts = toggleColumnShifts(
    state.columnDaysSelected,
    shiftsRows,
    state.shifts,
    day,
    firstDay,
    lastDay,
    view
  )

  const toggledColumnRestDays = toggleColumnRestDays(
    state.columnDaysSelected,
    shiftsRows,
    state.restDays,
    day,
    view
  )

  const toggledColumnDaySelected = xor(state.columnDaysSelected, [dayId])
  const isEnabled = Object.keys(toggledColumnDayByEmployee).length > 0

  return {
    ...resetGridSelectionMeta(state),
    leave: null,
    shifts: toggledColumnShifts,
    restDays: toggledColumnRestDays,
    daysByRow: toggledColumnDayByEmployee,
    columnDaysSelected: toggledColumnDaySelected,
    allTableSelected: false,
    isEnabled,
  }
}

const toggleShiftsRow = (state: SelectionState, shiftsRow: ShiftsRow, view: View) => {
  const days = view.dates
  const firstDay = view.dates[0] ?? moment()
  const lastDay = view.dates[view.dates.length - 1] ?? moment()
  const dayIds = workableDayIds(shiftsRow, days, view)

  const toggledRow = selectRow(state.rowsSelected, state.daysByRow, dayIds, shiftsRow.key)

  const toggledRowShifts: Shifts = toggleRowShifts(
    state.rowsSelected,
    state.shifts,
    shiftsRow,
    firstDay,
    lastDay
  )

  const toggledRowRestDays: RestDays = toggleRowRestDays(
    state.rowsSelected,
    state.restDays,
    shiftsRow,
    firstDay,
    lastDay
  )

  const togglerowsSelected = xor(state.rowsSelected, [shiftsRow.key])

  const isEnabled = Object.keys(toggledRow).length > 0

  return {
    ...resetGridSelectionMeta(state),
    leave: null,
    shifts: toggledRowShifts,
    restDays: toggledRowRestDays,
    daysByRow: toggledRow,
    rowsSelected: togglerowsSelected,
    allTableSelected: false,
    isEnabled,
  }
}

function toggleShift(shifts: Shifts, shift: Shift, selectedDay: moment.Moment): Shifts {
  const key = shift.referenceKey(selectedDay)
  const { [key]: discardedShift, ...keptShifts } = shifts

  if (discardedShift) {
    return keptShifts
  }

  return { ...shifts, [key]: shift }
}

function toggleRestDay(restDays: RestDays, restDay: RestDay): RestDays {
  const key = restDay.id
  const { [key]: discardedRestDay, ...keptRestDays } = restDays

  if (discardedRestDay) return keptRestDays

  return { ...restDays, [key]: restDay }
}

function addShifts(shiftsRow: ShiftsRow, shifts: Shifts, shift: Shift) {
  if (shiftsRow?.canManageOn(shift.date)) {
    if (shift.isNightShift) {
      shifts[shift.beginningReference] = shift

      if (shift.isOvernightByCalendarDay) {
        shifts[shift.endingReference] = shift
      }
    } else {
      shifts[shift.id] = shift
    }
  }

  return shifts
}

function addRestDays(shiftsRow: ShiftsRow, restDays: RestDays, restDay: RestDay) {
  if (shiftsRow?.canManageOn(restDay.date)) {
    restDays[restDay.id] = restDay
  }

  return restDays
}

function workableDayIds(shiftsRow: ShiftsRow, days: moment.Moment[], view: View): string[] {
  if (view.isByEmployees) {
    const filteredDays = days.filter((date: moment.Moment) => {
      const dayLeaves = shiftsRow.leaves.filterByDate(date)

      const workable = dayLeaves.filter(
        (leave: Leave) => leave.workable || leave.halfDay || leave.hourly || !leave.approved
      )

      const planningVersion = shiftsRow.planningVersions.planningVersionWithinDate(
        shiftsRow.id,
        date
      )

      return (
        planningVersion?.isShiftManagementTool && (dayLeaves.length === 0 || workable.length > 0)
      )
    })

    return filteredDays.map((date: moment.Moment) => dateToString(date, view.dateFormat))
  }

  // only if work area is not archived, that row is selectable
  if (shiftsRow.canManage) {
    return days.map((date: moment.Moment) => dateToString(date, view.dateFormat))
  }

  return []
}

const toggleAllShiftsRows = (state: SelectionState, shiftsRows: ShiftsRow[], view: View) => {
  if (state.allTableSelected) {
    return initialSelectionState()
  }

  const shifts: Shifts = {}
  const restDays: RestDays = {}
  const days = view.dates
  const firstDay = view.dates[0] ?? moment()
  const lastDay = view.dates[view.dates.length - 1] ?? moment()
  let daysByRow = {}

  const startAt = firstDay.clone().startOf('day')
  const endAt = lastDay.clone().endOf('day')
  shiftsRows.forEach((shiftsRow: ShiftsRow) => {
    if (shiftsRow.canManage) {
      const shiftsByRange = shiftsRow.shifts.filterByRange(startAt, endAt)
      shiftsByRange.shifts.forEach((shift: Shift) => {
        addShifts(shiftsRow, shifts, shift)
      })
      const restDaysByRange = shiftsRow.restDays.filterByRange(startAt, endAt)
      restDaysByRange.restDays.forEach((restDay: RestDay) => {
        addRestDays(shiftsRow, restDays, restDay)
      })
      const dayIds = workableDayIds(shiftsRow, days, view)
      daysByRow = selectRow([], daysByRow, dayIds, shiftsRow.key)
    }
  })

  const columnDaysSelected = days.map((day: moment.Moment) => dateToString(day, view.dateFormat))

  const rowsSelected = shiftsRows.map((shiftsRow: ShiftsRow) => shiftsRow.key)

  const isEnabled = Object.keys(daysByRow).length > 0

  return {
    ...resetGridSelectionMeta(state),
    leave: null,
    shifts,
    restDays,
    daysByRow,
    columnDaysSelected,
    rowsSelected,
    allTableSelected: true,
    isEnabled,
  }
}

export function initialSelectionState(): SelectionState {
  return {
    shifts: {},
    restDays: {},
    leave: null,
    daysByRow: {},
    isEnabled: false,
    columnDaysSelected: [],
    rowsSelected: [],
    allTableSelected: false,
    planningVersions: new PlanningVersions([]),
    showEmptyShiftCell: false,
    isResolvingGridSelection: false
  }
}

export default function reducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'CLEAR_SELECTION':
      return initialSelectionState()

    case 'SHOW_EMPTY_SHIFT_CELL':
      return {
        ...state,
        showEmptyShiftCell: true,
      }

    case 'HIDE_EMPTY_SHIFT_CELL':
      return {
        ...state,
        showEmptyShiftCell: false,
      }

    // These examples could help to understand the TOGGLE_SHIFT behavior
    // In state we already had '16-06-2022' toggled as day with no shifts

    // Example1: Toggle on a shift dayId '15-06-2022'
    // - existingDays => ['16-06-2022']
    // - daysFromShifts => ['15-06-2022']
    // - daysNotFromShifts => ['16-06-2022']
    // - employeeDays => ['15-06-2022', 16-06-2022']

    // Example2: Toggle off this shift again
    // - existingDays => ['15-06-2022', 16-06-2022']
    // - daysFromShifts => []
    // - daysNotFromShifts => ['16-06-2022']
    // - employeeDays => [16-06-2022']
    case 'TOGGLE_SHIFT': {
      // TODO: Find a way to simplify this:
      // The problem with this action is that we can't just toggle off a day
      // because we've toggled off a shift on that day. More than 1 shift might
      // be selected on that day so we still need the day to be selected.
      const { view, shift, selectedDay } = action
      const { dateFormat } = view

      const shiftKeyReference = shift.rowKeyReference(view)
      const dateKeyReference = shift.referenceKey(selectedDay)
      const datesFromShift = shift.datesByReference(dateKeyReference, view)
      const toggledDates = datesFromShift.map((date) => dateToString(date, dateFormat))

      const toggledShifts = toggleShift(state.shifts, shift, selectedDay)
      const shiftReferenceKeys = Object.keys(toggledShifts) || []
      const existingDays = state.daysByRow[shiftKeyReference] || []
      const toggledRestDays = Object.values(state.restDays)
      const daysFromRestDays: string[] = toggledRestDays.map((restDay) =>
        dateToString(restDay.date, dateFormat)
      )

      // Get all the days that are selected because of shifts
      let daysFromShifts: string[] = []
      shiftReferenceKeys.forEach((key) => {
        const shift = toggledShifts[key]
        const shiftKey = shift!.rowKeyReference(view)

        if (!shift || shiftKey != shiftKeyReference) return

        const datesFromShift = shift.datesByReference(key, view)
        const formattedDates = datesFromShift.map((date) => dateToString(date, dateFormat))

        daysFromShifts = daysFromShifts.concat(formattedDates)
      }, [])

      // Get the ones that are not on the list of selected shifts
      const daysOnlyFromShifts = difference(daysFromShifts, daysFromRestDays)
      const differenceDays = difference(existingDays, daysOnlyFromShifts)
      const daysNotFromShifts = difference(differenceDays, toggledDates)

      // When the rowDays for a row is empty, remove it from the
      // object instead of leaving the empty array.
      const rowDays = uniq([...daysNotFromShifts, ...daysFromRestDays, ...daysFromShifts])

      // When a day is toggled off, the object value reference is empty, we need
      // to remove it instead of leaving the empty array
      const toggledCells = flatten(
        Object.values({ ...state.daysByRow, [shiftKeyReference]: rowDays })
      )

      if (rowDays.length === 0 && toggledCells.length === 0) {
        const { [shiftKeyReference]: discarded, ...daysByRow } = state.daysByRow
        return {
          ...resetGridSelectionMeta(state),
          shifts: toggledShifts,
          daysByRow,
          isEnabled: false,
        }
      } else {
        const datesSet = new Set(toggledDates)
        return {
          ...resetGridSelectionMeta(state),
          leave: null,
          shifts: toggledShifts,
          daysByRow: { ...state.daysByRow, [shiftKeyReference]: rowDays },
          columnDaysSelected: state.columnDaysSelected.filter((date) => !datesSet.has(date)),
          rowsSelected: state.rowsSelected.filter((key: string) => key !== shiftKeyReference),
          allTableSelected: false,
          isEnabled: true,
        }
      }
    }

    case 'TOGGLE_REST_DAY': {
      const { restDay, view } = action
      const { dateFormat } = view

      const restDayKeyReference = restDay.employeeId.toString()
      const datesFromRestDay = restDay.datesByView(view)

      const toggledDates = datesFromRestDay.map((date) => dateToString(date, dateFormat))
      const toggledRestDays = toggleRestDay(state.restDays, restDay)
      const existingDays = state.daysByRow[restDayKeyReference] || []
      const toggledShifts = state.shifts
      const shiftReferenceKeys = Object.keys(toggledShifts) || []

      const daysFromShifts: string[] = []
      shiftReferenceKeys.forEach((key) => {
        const shift = toggledShifts[key]

        if (!shift) return

        daysFromShifts.push(dateToString(shift.dateByReference(key), dateFormat))
      }, [])

      const filteredRestDays = Object.values(toggledRestDays).filter(
        (restDay) => String(restDay.employeeId) === restDayKeyReference
      )
      const daysFromRestDays: string[] = filteredRestDays.flatMap((restDay) => {
        const datesFromRestDay = restDay.datesByView(view)
        return datesFromRestDay.map((date) => dateToString(date, dateFormat))
      })

      // Get the ones that are not on the list of selected rest days
      const daysOnlyFromRestDays = difference(daysFromRestDays, daysFromShifts)
      const differenceDays = difference(existingDays, daysOnlyFromRestDays)
      const daysNotFromRestDays = difference(differenceDays, toggledDates)

      // When the rowDays for a row is empty, remove it from the
      // object instead of leaving the empty array.
      const rowDays = uniq([...daysNotFromRestDays, ...daysFromShifts, ...daysFromRestDays])

      // When a day is toggled off, the object value reference is empty, we need
      // to remove it instead of leaving the empty array
      const toggledCells = uniq(
        flatten(Object.values({ ...state.daysByRow, [restDayKeyReference]: rowDays }))
      )
      if (rowDays.length === 0 && toggledCells.length === 0) {
        const { [restDayKeyReference]: discarded, ...daysByRow } = state.daysByRow
        return {
          ...resetGridSelectionMeta(state),
          restDays: toggledRestDays,
          daysByRow,
          isEnabled: false,
        }
      } else {
        const datesSet = new Set(toggledDates)
        return {
          ...resetGridSelectionMeta(state),
          leave: null,
          restDays: toggledRestDays,
          daysByRow: { ...state.daysByRow, [restDayKeyReference]: rowDays },
          columnDaysSelected: state.columnDaysSelected.filter((d) => !datesSet.has(d)),
          rowsSelected: state.rowsSelected.filter((key: string) => key !== restDayKeyReference),
          allTableSelected: false,
          isEnabled: true,
        }
      }
    }

    case 'TOGGLE_DAY': {
      const { dateFormat } = action.view
      const dayId = dateToString(action.day, dateFormat)

      const toggledDaysByRow = toggleDay(
        state.daysByRow,
        action.shiftsRow.key,
        dayId,
        action.forcedValue
      )

      // When a day is toggled off, the object value reference is empty, we need
      // to remove it instead of leaving the empty array
      const toggledCells = flatten(Object.values(toggledDaysByRow))
      const isEnabled = toggledCells.length > 0

      return {
        ...resetGridSelectionMeta(state),
        leave: null,
        daysByRow: toggledDaysByRow,
        columnDaysSelected: state.columnDaysSelected.filter((d: string) => d !== dayId),
        rowsSelected: state.rowsSelected.filter((key: string) => key !== action.shiftsRow.key),
        allTableSelected: false,
        isEnabled,
      }
    }

    case 'TOGGLE_DAY_COLUMN': {
      const view = action.view

      return toggleDayColumn(state, action.shiftsRows, action.day, view)
    }

    case 'TOGGLE_SHIFTS_ROW': {
      const view = action.view

      return toggleShiftsRow(state, action.shiftsRow, view)
    }

    case 'TOGGLE_ALL_SHIFTS_ROWS': {
      const view = action.view
      return toggleAllShiftsRows(state, action.shiftsRows, view)
    }

    case 'SELECT_RANGE': {
      const shiftRowKey = action.shiftsRow.key
      const days: moment.Moment[] = []

      let currentDay = action.startOn
      const lastDay = action.endOn
      while (currentDay <= lastDay) {
        days.push(currentDay)
        currentDay = currentDay.clone().add(1, 'day')
      }

      const { dateFormat } = action.view
      const dayIds = days.map((day) => dateToString(day, dateFormat))

      const daysByRow = { [shiftRowKey]: dayIds }

      return { ...resetGridSelectionMeta(state), daysByRow, isEnabled: true }
    }

    case 'TOGGLE_LEAVE': {
      const { leave } = action
      const initialState = initialSelectionState()

      if (leave.id === state.leave?.id) {
        return { ...initialState, leave: null }
      }

      return { ...initialState, leave }
    }

    case 'SET_FROM_GRID': {
      const hasSelection = action.daysByRow && Object.keys(action.daysByRow).length > 0
      if (!hasSelection) {
        return { ...initialSelectionState(), isEnabled: false }
      }
      return {
        ...initialSelectionState(),
        daysByRow: action.daysByRow,
        shifts: action.shifts ?? {},
        restDays: action.restDays ?? {},
        isEnabled: hasSelection,
        kind: action.kind,
        isResolvingGridSelection: false
      }
    }
    case 'RESOLVE_GRID_SELECTION': {
      return { ...state, isResolvingGridSelection: action.isResolvingGridSelection }
    }

    default:
      return state
  }
}
